import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Users, CheckCircle, XCircle, Briefcase, Wallet, Lock, Zap, Network, List, ChevronDown, ChevronRight, Crown } from 'lucide-react';

// Cycle of border/badge colors per depth level — same palette used on the
// admin "Team Tree" page, kept here so a user's own tree looks identical.
const LEVEL_COLORS = ['#22c55e', 'var(--primary)', '#a855f7', '#ef4444', '#3b82f6', '#ec4899'];

function formatCurrency(n) {
  return `₹${(n || 0).toLocaleString('en-IN')}`;
}

// One node in the org chart — identical component to the admin Team Tree
// page, minus the search-highlight bits (a user's own downline is small
// enough not to need a jump-to-user search).
function TreeNode({ node, depth, expanded, toggleExpand }) {
  const hasChildren = node.children && node.children.length > 0;
  const isOpen = expanded.has(node._id);
  const color = node.role === 'admin' ? '#22c55e' : LEVEL_COLORS[depth % LEVEL_COLORS.length];
  const showChildren = hasChildren && isOpen;

  return (
    <div className="ot-branch">
      <div className="org-node" style={{ borderColor: `${color}55` }}>
        <div className="org-node-avatar" style={{ background: `${color}22`, color, borderColor: color }}>
          {node.role === 'admin' ? <Crown size={16} /> : (node.name?.[0] || '?').toUpperCase()}
        </div>
        <div className="org-node-name">{node.name}</div>
        <div className="org-node-code">{node.referralCode}</div>
        {node.isBlocked && <span className="badge badge-danger" style={{ fontSize: '0.6rem', padding: '2px 6px' }}>Blocked</span>}
        <div className="org-node-row">
          <span className={`badge ${node.isActive ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '0.62rem', padding: '2px 8px' }}>
            {node.isActive ? 'Active' : 'Inactive'}
          </span>
          <span style={{ fontSize: '0.68rem', color: 'var(--primary)', fontWeight: 700 }}>
            {node.activePackage > 0 ? formatCurrency(node.activePackage) : '—'}
          </span>
        </div>
        <div className="org-node-stats">
          <span title="Team size">Team: {node.totalTeam || 0}</span>
          <span title="Team business">{formatCurrency(node.totalBusiness)}</span>
        </div>

        {hasChildren && (
          <button
            type="button"
            className="org-node-toggle"
            onClick={() => toggleExpand(node._id)}
            title={isOpen ? 'Collapse' : `Expand (${node.children.length})`}
          >
            {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {node.children.length}
          </button>
        )}
      </div>

      {showChildren && (
        <>
          <div className="ot-vline" />
          <div className="ot-children-row">
            {node.children.map((child, idx) => (
              <div
                key={child._id}
                className={`ot-child-col ${idx === 0 ? 'ot-first' : ''} ${idx === node.children.length - 1 ? 'ot-last' : ''}`}
              >
                <div className="ot-child-col-stem" />
                <TreeNode node={child} depth={depth + 1} expanded={expanded} toggleExpand={toggleExpand} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Auto-expands the root + first couple of generations, same as admin page,
// so the tree isn't a single collapsed dot when it first loads.
function collectIdsUpToDepth(node, depth, acc) {
  if (!node || depth < 0) return acc;
  acc.add(node._id);
  if (node.children?.length) node.children.forEach(c => collectIdsUpToDepth(c, depth - 1, acc));
  return acc;
}

export default function Team() {
  const { user } = useAuth();
  const [view, setView] = useState('list'); // 'list' | 'tree'
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ===================== Team Tree (this user's own downline) =====================
  const [treeLoading, setTreeLoading] = useState(true);
  const [treeRoot, setTreeRoot] = useState(null);
  const [treeTotal, setTreeTotal] = useState(0);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    api.get('/users/team').then(r => setTeamData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const fetchMyTree = useCallback(() => {
    setTreeLoading(true);
    api.get('/users/my-team-tree')
      .then(r => {
        setTreeRoot(r.data.root);
        setTreeTotal(r.data.totalTeam);
        setExpanded(collectIdsUpToDepth(r.data.root, 2, new Set()));
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load team tree'))
      .finally(() => setTreeLoading(false));
  }, []);

  useEffect(() => { if (view === 'tree' && !treeRoot) fetchMyTree(); }, [view, treeRoot, fetchMyTree]);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    if (!treeRoot) return;
    const all = new Set();
    const walk = (n) => { all.add(n._id); n.children?.forEach(walk); };
    walk(treeRoot);
    setExpanded(all);
  };

  const collapseAll = () => setExpanded(new Set(treeRoot ? [treeRoot._id] : []));

  return (
    <DashboardLayout>
      <h1 className="page-title">My Team</h1>
      <p className="page-subtitle">Manage your network and track team performance</p>

      {/* Referral Box — locked until the user has an active package */}
      {user?.isActive ? (
        <div className="referral-box" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Your Referral Code</div>
              <div className="referral-code-display">{user?.referralCode}</div>
              <div style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>Share this code to earn direct income</div>
            </div>
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 6 }}>Your Referral Link</div>
              <div className="copy-link">
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {window.location.origin}/register/{user?.referralCode}
                </span>
                <button className="copy-btn" onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/register/${user?.referralCode}`);
                  alert('Link Copied!');
                }}>Copy</button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="referral-box referral-box-locked" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div className="referral-lock-icon"><Lock size={20} /></div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 700, color: 'var(--text-white)', marginBottom: 4 }}>Your Referral Link is Locked</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>Activate a package to unlock your referral code and start building your team.</div>
            </div>
            <Link to="/dashboard/package" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Zap size={14} /> Activate Package
            </Link>
          </div>
        </div>
      )}

      {/* Team Stats */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        {[
          { icon: Users, label: 'Direct Members', value: teamData?.totalDirects || 0, color: 'var(--primary)' },
          { icon: CheckCircle, label: 'Active Members', value: teamData?.totalActive || 0, color: '#22c55e' },
          { icon: XCircle, label: 'Inactive Members', value: (teamData?.totalDirects || 0) - (teamData?.totalActive || 0), color: '#ef4444' },
          { icon: Briefcase, label: 'Total Business', value: formatCurrency(teamData?.totalBusiness || user?.totalBusiness), color: '#3b82f6' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-icon" style={{ background: `${s.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color }}><s.icon size={22} /></div>
            <div className="stat-info">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Direct Income Rates */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Wallet size={18} /> Direct Income Rates</h3></div>
        <div className="grid-3">
          {[{ lvl: 'Level 1', rate: '7%', desc: 'Direct referral' }, { lvl: 'Level 2', rate: '2%', desc: 'Referral of referral' }, { lvl: 'Level 3', rate: '1%', desc: '3rd level' }].map(l => (
            <div key={l.lvl} className="wallet-card" style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ fontFamily: 'Rajdhani', fontSize: '2rem', fontWeight: 700, color: 'var(--primary)' }}>{l.rate}</div>
              <div style={{ fontWeight: 600, color: 'var(--text-white)', marginBottom: 4 }}>{l.lvl}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{l.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* List / Tree toggle — same idea as the admin Team page's tabs */}
      <div className="tab-bar" style={{ marginBottom: 16 }}>
        <button className={`tab-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <List size={14} /> Direct Members
        </button>
        <button className={`tab-btn ${view === 'tree' ? 'active' : ''}`} onClick={() => setView('tree')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Network size={14} /> Team Tree
        </button>
      </div>

      {view === 'list' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Users size={18} /> Direct Members</h3>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{teamData?.directs?.length || 0} members</div>
          </div>
          {loading ? (
            <div className="loader-wrap"><div className="loader"></div></div>
          ) : !teamData?.directs?.length ? (
            <div className="empty-state">
              <div className="empty-icon"><Users size={40} /></div>
              <h3>No team members yet</h3>
              <p style={{ fontSize: '0.85rem' }}>Share your referral code to build your team</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="tp-table">
                <thead>
                  <tr><th>#</th><th>Name</th><th>Mobile</th><th>Referral Code</th><th>Package</th><th>Status</th><th>Team</th><th>Joined</th></tr>
                </thead>
                <tbody>
                  {teamData.directs.map((member, i) => (
                    <tr key={member._id}>
                      <td style={{ color: 'var(--text-muted)' }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{member.name}</td>
                      <td style={{ fontSize: '0.85rem' }}>{member.mobile}</td>
                      <td><span style={{ color: 'var(--primary)', fontFamily: 'monospace', letterSpacing: '1px' }}>{member.referralCode}</span></td>
                      <td style={{ color: 'var(--primary)', fontWeight: 700 }}>
                        {member.activePackage > 0 ? `₹${member.activePackage.toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td>
                        <span className={`badge ${member.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {member.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{member.totalTeam || 0}</td>
                      <td style={{ fontSize: '0.82rem' }}>{new Date(member.createdAt).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === 'tree' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Network size={18} /> My Team Tree</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{treeTotal} in your downline</div>
              {treeRoot && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={expandAll}>Expand All</button>
                  <button type="button" className="btn btn-outline btn-sm" onClick={collapseAll}>Collapse All</button>
                </div>
              )}
            </div>
          </div>

          {treeLoading ? (
            <div className="loader-wrap"><div className="loader"></div></div>
          ) : !treeRoot ? (
            <div className="empty-state"><div className="empty-icon"><Users size={40} /></div><h3>No team data yet</h3></div>
          ) : (
            <div className="org-tree-wrap">
              <div className="ot-roots-row">
                <TreeNode node={treeRoot} depth={0} expanded={expanded} toggleExpand={toggleExpand} />
              </div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}